import React, {Component} from 'react'
import {
  ChakraProvider,
  theme,
  useToast,
} from '@chakra-ui/react';
import { 
  BrowserRouter as Router, 
  Routes,
  Route, 
  Outlet,
  Navigate,
  useNavigate,
  useOutletContext
} from 'react-router-dom';

import './styles/fonts.css';
import { useCsrf } from './hooks/useCsrf';

import Authentication from './subsystems/Authentication/Authentication'
import BooksIndex from './subsystems/Main/BooksIndex'
import MainPage from './subsystems/Main/Main'
import ChapterIndexWrapper from './subsystems/Main/ChapterIndex'
import Payments from './subsystems/Payments/Payments'
import Account from './subsystems/Navigation/Account'
import Explore from './subsystems/Explore/Explore'
import StoryView from './subsystems/Explore/StoryView'
import About from './subsystems/About/About'
import Practice from './subsystems/Practice/Practice'
import Landing from './subsystems/Landing/Landing'


function App() {
  
  return (
    <ChakraProvider theme={theme}>
      <Router>
        <Routes>
          <Route element = {<AppUIRoute/>}>
            <Route index element = {<Landing />}/>
            <Route path = "login" element = {<Authentication route = 'login'/>}/>
            <Route path = "register" element = {<Authentication route = 'register'/>}/>
            <Route path = "forgot_password" element = {<Authentication route = 'forgot_password'/>}/>
            <Route path = "verify_password_reset_token" element = {<Authentication route = 'verifyPasswordResetToken'/>}/>
            <Route path = "reset_password" element = {<Authentication route = 'resetPassword'/>}/>

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="book/:bookId" element={<MainPage />} />
              <Route path="chapter/:chapterId" element={<ChapterIndexWrapper />} />
              <Route path="index" element={<BooksIndex />} />
              <Route path="account" element={<Account />} />
              <Route path="payments" element={<Payments />} />
              <Route path="practice" element={<Practice />} />
            </Route>
            <Route path = "explore" element = {<Explore/>}/>
            <Route path = "story/:storyId" element = {<StoryView/>}/>
            <Route path = "author/:authorName" element = {<Explore isAuthorView={true}/>}/>
            <Route path = "about" element = {<About/>}/>
          </Route>
        </Routes>
      </Router>
    </ChakraProvider>
  );
}

const ProtectedRoute = () => {
  const context = useOutletContext();
  const {isAuthenticated} = context;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet context = {context}/>;
};

function AppUIRoute() {
  const navigate = useNavigate();
  const toast = useToast();
  return (
    <AppUIWithCsrf navigate = {navigate} toast = {toast}/> 
 )
}

function AppUIWithCsrf(props) {
  const { csrfToken, isLoading, authenticatedFetch } = useCsrf();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return <AppUI {...props} csrfToken={csrfToken} authenticatedFetch={authenticatedFetch} />;
}


export const backendURL = process.env.REACT_APP_BACKEND_URL || ''

const placeHolderSnippetGenerator = {
  prompt: '',
  response: {
    active: false,
    text: ''
  }
}

const initialLoginDetails = {
  username: '',
  email: '',
  password: '',
}

const initialAccountDetails = {
  username: '',
  email: '',
  password: '',
}

const initialState = {
  loginDetails: initialLoginDetails,
  accountDetails: initialAccountDetails,
  route: 'login',
  books: {
    exists: false,
    items: [],
    updates: {}
  },
  chapters: {
    book_id: 1,
    exists: false,
    items: []
  },
  chapter: {
    chapter_id: 1,
    snippets: {
      exists: false,
      items: []
    },
    updates: {},
  },
  isGenerating: false
}



class AppUI extends Component {

  constructor(props) {
    super(props)
    this.state = {
      ...initialState,
      isAuthenticated: false,
    }
  }

  /**
   * Helper for POST requests — includes CSRF token automatically via authenticatedFetch.
   */
  postFetch = async (url, body = null) => {
    const options = {
      method: 'POST',
      ...(body !== null ? { body: JSON.stringify(body) } : {}),
    };
    const response = await this.props.authenticatedFetch(backendURL + url, options);
    return await response.json();
  }

  componentDidMount() {
    this.fetchAuthStatus();
  }

  fetchAuthStatus = async () => {
    try {
      const response = await fetch(backendURL + '/explore/auth_status', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      this.setState({ isAuthenticated: data.isAuthenticated });
    } catch (error) {
      this.setState({ isAuthenticated: false });
    }
  }

  onNameChange = (value) => {
      this.setState({
        username: value
      })
  }

  onEmailChange = (value) => {
    this.setState({
      email: value
    })
}

  onPasswordChange = (value) => {
    this.setState({
      password: value
    })
  }

  onRouteChange = (dest) => {
    // this.setState({
    //   route: dest
    // })
    this.props.navigate(`/${dest}`);
  }

  onFormTextChange = (form, key, value) => {
    this.setState({
      [form]: {
        ...this.state[form],
        [key]: value
      }
    })
  }

  onPaginationButtonClick = (value, loc) => {
    let page = this.state[loc].page
    let add = 0
    if (value === 'Next') {
      add += 1
    } else {
      add += -1
    }
    this.setState({
      [loc]: {
        ...this.state[loc],
        page: page += add
      }
    }) 
  }

  onFormTextChangeNested4 = (level_1, level_2, key, value) => {
  //   this.setState({
  //     [level_1]: {
  //       ...this.state[level_1],
  //       [level_2]: {
  //         ...this.state[level_2],
  //         [key]: value
  //       }
  //     }
  //   })
  // }
    this.setState({
      [level_1]: {
        ...this.state[level_1],
        [level_2]: {
          ...this.state[level_1][level_2],
          [key]: value
        }
      }
    })
  }

  onFormTextChangeUpdateAcctInfo = (level_1, type, value) => {
    this.setState({
      [level_1]: {
        ...this.state[level_1],
        updates: {
          ...this.state[level_1]['updates'],
            [type]: value
        }
      }
    })
  }

  onFormTextChangeUpdate = (level_1, snippet_id, type, value) => {
      this.setState({
        [level_1]: {
          ...this.state[level_1],
          updates: {
            ...this.state[level_1]['updates'],
            [snippet_id]: {
              ...this.state[level_1]['updates'][snippet_id],
              [type]: value
            }
          }
        }
      })
    }

    onFormTextChangeChapterTitle = (level_1, snippet_id, type, value) => {
      this.setState({
        [level_1]: {
          ...this.state[level_1],
          updates: {
            ...this.state[level_1]['updates'],
            [snippet_id]: {
              [type]: value
            }
          }
        }
      })
    }

    onFormTextChangeBookTitle = (level_1, snippet_id, type, value) => {
      this.setState({
        [level_1]: {
          ...this.state[level_1],
          updates: {
            ...this.state[level_1]['updates'],
            [snippet_id]: {
              [type]: value
            }
          }
        }
      })
    }

  onGeneratePage = () => {
    this.setState({
      route: 'generatePage',
      snippetGenerator: placeHolderSnippetGenerator
    })
  }

  onClickChangePassword = (type) => {
    this.setState({
      accountDetails: {
        ...this.state.accountDetails,
        editingPassword: type
      }
    })
  }

  onLogOut = async () => {
    try {
      let response = await this.postFetch('/logout');
      let { success, message } = response;
      if (success) {
        this.props.toast && this.props.toast({
          title: 'Logged out',
          description: message,
          position: 'top',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        this.setState(initialState);
        this.setState({ isAuthenticated: false });
        this.props.navigate('/login');
      } else {
        this.props.toast && this.props.toast({
          title: 'Error',
          description: message,
          position: 'top',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      this.props.toast && this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  onRegister = async () => {
    try {
      let response = await this.postFetch('/register', {
        user: this.state.loginDetails.username,
        email: this.state.loginDetails.email,
        password: this.state.loginDetails.password
      })
      let {success, message, result} = response
      if (success === true) {
        let {account, books} = result
        this.props.toast({
          title: 'Success',
          description: message,
          position: 'top',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        this.setState({
          ...this.state,
          accountDetails: account,
          books: books,
          isAuthenticated: true
        })
        this.props.navigate('/index');
        await this.fetchAuthStatus();
      } else {
        this.props.toast({
          title: 'Error',
          description: message,
          position: 'top',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      } 
    } catch (error) {
        this.props.toast({
          title: 'Error',
          description: error.message,
          position: 'top',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
  }


  checkSignIn = async () => {
    try {
      let response = await this.postFetch('/check_signin', {
        username: this.state.loginDetails.username,
        password: this.state.loginDetails.password
      })
      let {success, message, result} = response
      if (success === true) {
        let {account, books} = result
        this.setState({
          ...this.state,
          accountDetails: account,
          books: books,
          isAuthenticated: true
        })
        this.props.navigate('/index');
      } else {
        this.props.toast({
          title: 'Error',
          description: message,
          position: 'top',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    } catch (error) {
        this.props.toast({
          title: 'Error',
          description: error.message,
          position: 'top',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
    }
  }

  generateText = async (snippet_id) => {
    this.setState({
      ...this.state,
        isGenerating: true
    })
    try {
      let response = await this.postFetch('/generate_text', {
        book_id: this.state.chapters.book_id,
        chapter_id: this.state.chapter.chapter_id,
        snippet_id: snippet_id,
        is_outline: this.state.chapter.is_outline,
        prompt: this.state.chapter.updates[snippet_id]['prompt'],
        options: {
          tone: this.state.chapter.updates[snippet_id]['tone'] || '',
          writing_style: this.state.chapter.updates[snippet_id]['writingStyle'] || '',
          pov: this.state.chapter.updates[snippet_id]['pov'] || '',
          language: this.state.chapter.updates[snippet_id]['language'] || '',
          creativity: this.state.chapter.updates[snippet_id]['creativity'] ?? 50,
          max_words: this.state.chapter.updates[snippet_id]['maxWords'] ?? 300,
          model: this.state.chapter.updates[snippet_id]['model'] || '',
        }
      })
      let {success, message, result} = response
      let successMessage = success ? 'Success' : 'Error'
      let status = success ? 'success' : 'error'
      if (success === true) {
        let {chapter, chapters} = result
        this.setState({
          ...this.state,
          chapter: chapter,
          chapters: chapters,
          isGenerating: false
        })
        this.props.toast({
          title: successMessage,
          description: message,
          position: 'top',
          status: status,
          duration: 3000,
          isClosable: true,
        })
      } else {
        this.setState({
          ...this.state,
          isGenerating: false
        })
        this.props.toast({
          title: successMessage,
          description: message,
          position: 'top',
          status: status,
          duration: 3000,
          isClosable: true,
        })
      }
    } catch (error) {
        this.props.toast({
          title: 'Error',
          description: error.message,
          position: 'top',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
    }
    
  }

  onSaveSnippet = async (snippet_id, textOverride) => {
    try {
      let response = await this.postFetch('/save_snippet', {
        book_id: this.state.chapters.book_id,
        chapter_id: this.state.chapter.chapter_id,
        snippet_id: snippet_id,
        text: textOverride !== undefined ? textOverride : this.state.chapter.updates[snippet_id]['text']
      })
      let {result} = response
      let {chapter, chapters} = result
      this.setState({
        ...this.state,
        chapter: chapter,
        chapters: chapters
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onViewChapter = async (chapter_id) => {
    try {
      let response = await this.postFetch('/view_chapter', {
        book_id: this.state.chapters.book_id,
        chapter_id: chapter_id
      })
      let {result} = response
      let {chapter, chapters} = result
      this.setState({
        ...this.state,
        // route: 'chapterIndex',
        chapter: chapter,
        chapters: chapters
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onEditSnippet = async (snippet_id) => {
    let route = snippet_id === 'outline' ? '/edit_outline' : '/edit_snippet'
    let snippet_id_send = snippet_id === 'outline' ? 'outline' : snippet_id
    try {
      let response = await this.postFetch(route, {
        chapter_id: this.state.chapter.chapter_id,
        snippet_id: snippet_id_send,
        book_id: this.state.chapters.book_id
      })
      let {result} = response
      let {chapter, chapters} = result
      this.setState({
        ...this.state,
        chapter: chapter,
        chapters: chapters
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onCancelEditSnippet = async (snippet_id) => {
    try {
      let response = await this.postFetch('/cancel_edit_snippet', {
        book_id: this.state.chapters.book_id,
        chapter_id: this.state.chapter.chapter_id,
        snippet_id: snippet_id,
      })
      let { result } = response
      let {chapter, chapters} = result
      this.setState({
        ...this.state,
        chapter: chapter,
        chapters: chapters
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onDeleteSnippet = async (snippet_id) => {
    try {
      let response = await this.postFetch('/delete_snippet', {
        book_id: this.state.chapters.book_id,
        chapter_id: this.state.chapter.chapter_id,
        snippet_id: snippet_id
      })
      let { result } = response
      let {chapter, chapters} = result
      this.setState({
        ...this.state,
        chapter: chapter,
        chapters: chapters
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onDeleteChapter = async (chapter_id) => {
    try {
      let response = await this.postFetch('/delete_chapter', {
        chapter_id: chapter_id,
        book_id: this.state.chapters.book_id
      })
      let { result } = response
      this.setState({
        ...this.state,
        chapters: result,
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onCreateSnippet = async (chapter_id) => {
    try {
      let response = await this.postFetch('/create_snippet', {
        chapter_id: chapter_id,
        book_id: this.state.chapters.book_id
      })
      let { success, message, result } = response
      let {chapter, chapters} = result
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'
      this.setState({
        ...this.state,
        chapter: chapter,
        chapters: chapters
      })
      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 3000,
        isClosable: true,
      })  
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onCreateChapter = async () => {
    try {
      let response = await this.postFetch('/create_chapter', {
        book_id: this.state.chapters.book_id
      })
      let { success, message, result } = response
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'
      this.setState({
        ...this.state,
        chapters: result,
      })
      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 3000,
        isClosable: true,
      })  
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onCreateBook = async () => {
    try {
      let response = await this.postFetch('/create_book')
      let { success, message, result } = response
      this.setState({
        ...this.state,
        books: result,
      })
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'
      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onViewBook = async (book_id) => {
    try {
      let response = await this.postFetch('/view_book', {
        book_id: book_id
      })
      let { result } = response
      this.setState({
        ...this.state,
        route: 'mainPage',
        chapters: result,
      })
      this.props.navigate(`/book/${book_id}`);
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onDeleteBook = async (book_id) => {
    try {
      let response = await this.postFetch('/delete_book', {
        book_id: book_id
      })
      let { result } = response
      this.setState({
        ...this.state,
        books: result,
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onSaveBookTitle = async (book_id) => {
    try {
      let response = await this.postFetch('/save_book_title', {
        book_id: book_id,
        new_title: this.state.books.updates[book_id]['title']
      })
      let { result } = response
      this.setState({
        ...this.state,
        books: result,
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onSaveChapterTitle = async (chapter_id) => {
    try {
      let response = await this.postFetch('/save_chapter_title', {
        book_id: this.state.chapters.book_id,
        chapter_id: chapter_id,
        new_title: this.state.chapter.updates[chapter_id]['title']
      })
      let { result } = response
      this.setState({
        ...this.state,
        chapters: result,
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onChangeAccountInfo = async (type) => {
    try {
      let response = await this.postFetch('/change_account_info', {
        type: type,
        new_value: this.state.accountDetails.updates[type]
      })
      let { success, message, result } = response
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'
      this.setState({
        ...this.state,
        accountDetails: result,
      })
      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onSaveNewPassword = async (type) => {
    try {
      let response = await this.postFetch('/save_new_password', {
        current_password: this.state.accountDetails.updates['current_password'],
        new_password_1: this.state.accountDetails.updates['new_password_1'],
        new_password_2: this.state.accountDetails.updates['new_password_2'],
      })
      let { success, message } = response
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'

      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 3000,
        isClosable: true,
      })
      if (success) {
        this.setState({
          accountDetails: {
            ...this.state.accountDetails,
            editingPassword: false
          }
        })
      }
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onChangeSubscription = async (type) => {
    let accountDetails = this.state.accountDetails
    try {
      let response = await this.postFetch('/cancel_subscription', {
        subscr_info: accountDetails.subscr_info,
        type: type
      })
      let { success, message, result } = response
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'
      
      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 10000,
        isClosable: true,
      })
      if (success) {
        this.setState({
          ...this.state,
          accountDetails: result,
        })
      }
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onSendResetEmail = async (type) => {
    try {
      let response = await this.postFetch('/on_send_reset_email', {
        email: this.state.loginDetails.email
      })
      let { success, message } = response
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'

      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 10000,
        isClosable: true,
      })
      if (success) {
        this.setState({
          ...this.state,
          // route: 'verifyPasswordResetToken',
        })
        this.props.navigate('/verify_password_reset_token');
      }
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onVerifyPasswordResetToken = async () => {
    try {
      let response = await this.postFetch('/verify_password_reset_token', {
        token: this.state.loginDetails.token
      })
      let { success, message } = response
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'
      
      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 10000,
        isClosable: true,
      })
      if (success) {
        this.setState({
          ...this.state,
          // route: 'resetPassword',
        })
        this.props.navigate('/reset_password');
      }
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onResetPassword = async () => {
    try {
      let response = await this.postFetch('/reset_password', {
        token: this.state.loginDetails.token,
        new_password_1: this.state.loginDetails.new_pw1,
        new_password_2: this.state.loginDetails.new_pw2,
      })
      let { success, message } = response
      let status = success ? 'success' : 'warning'
      let title = success ? 'Success' : 'Error'

      this.props.toast({
        title: title,
        description: message,
        position: 'top',
        status: status,
        duration: 10000,
        isClosable: true,
      })
      if (success) {
        this.setState({
          ...this.state,
          // route: 'login',
        })
        this.props.navigate('/login');
      }
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onRewriteText = async (snippet_id, selected_text, full_text, instruction, selection_range) => {
    try {
      let response = await this.postFetch('/rewrite_text', {
        book_id: this.state.chapters.book_id,
        chapter_id: this.state.chapter.chapter_id,
        snippet_id: snippet_id,
        selected_text: selected_text,
        full_text: full_text,
        instruction: instruction,
        selection_start: selection_range?.start,
        selection_end: selection_range?.end,
        options: {}
      })
      let {success, message, result} = response
      if (success) {
        let {chapter, chapters} = result
        this.setState({
          ...this.state,
          chapter: chapter,
          chapters: chapters
        })
        this.props.toast({
          title: 'Success',
          description: message,
          position: 'top',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } else {
        this.props.toast({
          title: 'Error',
          description: message,
          position: 'top',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  onGetPracticeFeedback = async (assignment, text, extraInstructions) => {
    try {
      let response = await this.postFetch('/practice_feedback', {
        assignment: assignment,
        text: text,
        extra_instructions: extraInstructions || ''
      })
      let { success, message, result } = response
      if (success) {
        return result.feedback
      } else {
        this.props.toast({
          title: 'Error',
          description: message,
          position: 'top',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
        return null
      }
    } catch (error) {
      this.props.toast({
        title: 'Error',
        description: error.message,
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return null
    }
  }

  render() {
    return (
      <>
      <Outlet
        context = {{
          state: this.state,
          onNameChange: this.onNameChange,
          onEmailChange: this.onEmailChange,
          onPasswordChange: this.onPasswordChange,
          onRouteChange: this.onRouteChange,
          onLogOut: this.onLogOut,
          onFormTextChange: this.onFormTextChange,
          onPaginationButtonClick: this.onPaginationButtonClick,
          onFormTextChangeNested4: this.onFormTextChangeNested4,
          onFormTextChangeUpdateAcctInfo: this.onFormTextChangeUpdateAcctInfo,
          onFormTextChangeUpdate: this.onFormTextChangeUpdate,
          onFormTextChangeChapterTitle: this.onFormTextChangeChapterTitle,
          onFormTextChangeBookTitle: this.onFormTextChangeBookTitle,
          onGeneratePage: this.onGeneratePage,
          onClickChangePassword: this.onClickChangePassword,
          onRegister: this.onRegister,
          checkSignIn: this.checkSignIn,
          generateText: this.generateText,
          onSaveSnippet: this.onSaveSnippet,
          onViewChapter: this.onViewChapter,
          onEditSnippet: this.onEditSnippet,
          onCancelEditSnippet: this.onCancelEditSnippet,
          onDeleteSnippet: this.onDeleteSnippet,
          onDeleteChapter: this.onDeleteChapter,
          onCreateSnippet: this.onCreateSnippet,
          onCreateChapter: this.onCreateChapter,
          onCreateBook: this.onCreateBook,
          onViewBook: this.onViewBook,
          onDeleteBook: this.onDeleteBook,
          onSaveBookTitle: this.onSaveBookTitle,
          onSaveChapterTitle: this.onSaveChapterTitle,
          onChangeAccountInfo: this.onChangeAccountInfo,
          onSaveNewPassword: this.onSaveNewPassword,
          onChangeSubscription: this.onChangeSubscription,
          onSendResetEmail: this.onSendResetEmail,
          onVerifyPasswordResetToken: this.onVerifyPasswordResetToken,
          onResetPassword: this.onResetPassword,
          onRewriteText: this.onRewriteText,
          onGetPracticeFeedback: this.onGetPracticeFeedback,
          isAuthenticated: this.state.isAuthenticated,
          fetchAuthStatus: this.fetchAuthStatus,
          authenticatedFetch: this.props.authenticatedFetch
        }}
      />
      </>
    )
  }
}

export default App;
